import React, { useState } from 'react';
import { activePb } from '../../lib/pb';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import SlideOver from '../../shared/components/SlideOver';
import FileDropzone from '../../shared/components/FileDropzone';
import AddressAutocomplete from '../../shared/components/AddressAutocomplete';
import { toLocalInput, fromLocalInput, defaultWindow } from '../../shared/datetime';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

const STATUS_OPTIONS = [
  ['scheduled', 'Scheduled'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
  ['requested', 'Requested by client'],
  ['reschedule_requested', 'Reschedule requested'],
];

const EMPTY = {
  title: '',
  client_id: '',
  property_id: '',
  scheduled_start: '',
  scheduled_end: '',
  status: 'scheduled',
  engineer_id: '',
  site_line1: '',
  site_line2: '',
  site_city: '',
  site_postcode: '',
  site_country: '',
  site_lat: null,
  site_lng: null,
  description: '',
  notes: '',
};

function buildInitialForm(job, clientId) {
  if (job) {
    return {
      title: job.title || '',
      client_id: job.client_id || '',
      property_id: job.property_id || '',
      status: job.status || 'scheduled',
      engineer_id: job.engineer_id || '',
      scheduled_start: toLocalInput(job.scheduled_start),
      scheduled_end: toLocalInput(job.scheduled_end),
      site_line1: job.site_line1 || '',
      site_line2: job.site_line2 || '',
      site_city: job.site_city || '',
      site_postcode: job.site_postcode || '',
      site_country: job.site_country || '',
      site_lat: job.site_lat ?? null,
      site_lng: job.site_lng ?? null,
      description: job.description || '',
      notes: job.notes || '',
    };
  }
  return { ...EMPTY, client_id: clientId || '' };
}

// Canonical job create/edit form — single source of truth for job editing
// (PLAN_admin_portal_phase1.md §3/§5). Used in three contexts:
//   variant="drawer"  — Schedule calendar/map + client Work History (in-place)
//   variant="page"    — /admin/jobs/new and /admin/jobs/:id/edit (full page)
// Property select prefills the site address/coordinates (migration 021);
// the site fields remain editable as the per-job override.
export default function JobForm({ open, onClose, job = null, clientId = '', variant = 'drawer', onSaved }) {
  const { data, add, update } = useData();
  const { notify } = useToast();

  const [form, setForm] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  // Reset-on-open (drawer) / init-on-mount (page): re-initialise the form
  // whenever a different job is opened or the drawer is reopened.
  const [openedKey, setOpenedKey] = useState(null);
  const formKey = variant === 'page' ? job?.id || '__new__' : (open ? job?.id || '__new__' : null);
  if (formKey && formKey !== openedKey) {
    setOpenedKey(formKey);
    setForm(buildInitialForm(job, clientId));
    setPhotoFiles([]);
  }

  // First render pass after open: form is still null — guard like the old
  // JobEditModal (crash reported on /admin/schedule previously).
  if (!form) return null;

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const client = data.clients.find((item) => item.id === form.client_id);
  const clientProperties = (form.client_id
    ? data.properties.filter((p) => p.client_id === form.client_id && p.status !== 'archived')
    : []);
  const siteAddressLine = [form.site_line1, form.site_line2, form.site_city, form.site_postcode].filter(Boolean).join(', ');

  const selectClient = (clientIdValue) => {
    setForm((current) => ({
      ...current,
      client_id: clientIdValue,
      property_id: '',
      // Site belongs to the new client — clear it so a stale address can't
      // silently persist across clients.
      site_line1: '',
      site_line2: '',
      site_city: '',
      site_postcode: '',
      site_country: '',
      site_lat: null,
      site_lng: null,
    }));
  };

  // Selecting a property prefills the site address + coordinates. The site
  // fields remain editable as the per-job override (properties move, jobs
  // snapshot their location at the time).
  const selectProperty = (propertyId) => {
    const property = data.properties.find((item) => item.id === propertyId);
    setForm((current) => ({
      ...current,
      property_id: propertyId,
      site_line1: property?.address_line1 ?? '',
      site_line2: property?.address_line2 ?? '',
      site_city: property?.city ?? '',
      site_postcode: property?.postcode ?? '',
      site_country: property?.country ?? '',
      site_lat: property?.lat ?? null,
      site_lng: property?.lng ?? null,
    }));
  };

  const applySitePlace = (place) => setForm((current) => ({
    ...current,
    site_line1: place.line1 || current.site_line1,
    site_line2: place.line2 || '',
    site_city: place.city || '',
    site_postcode: place.postcode || '',
    site_country: place.country || '',
    site_lat: place.lat ?? current.site_lat,
    site_lng: place.lng ?? current.site_lng,
  }));

  const copyClientAddress = () => {
    if (!client) return;
    setForm((current) => ({
      ...current,
      site_line1: client.address_line1 || '',
      site_line2: client.address_line2 || '',
      site_city: client.city || '',
      site_postcode: client.postcode || '',
      site_country: client.country || '',
      site_lat: client.lat ?? null,
      site_lng: client.lng ?? null,
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.client_id) {
      notify('Job title and client are required', 'error');
      return;
    }
    setBusy(true);
    try {
      const window = defaultWindow(form.scheduled_start?.slice(0, 10));

      // Accept flow (pass 6 §6C): staff confirm a client request by giving
      // the job a real slot — status flips to scheduled and the client's
      // request metadata is cleared (matches the calendar drag).
      const isPendingRequest = job?.status === 'requested' || job?.status === 'reschedule_requested';
      const hasSlot = Boolean(form.scheduled_start);
      const nextStatus = isPendingRequest && hasSlot ? 'scheduled' : form.status;

      const payload = {
        title: form.title.trim(),
        client_id: form.client_id,
        property_id: form.property_id,
        status: nextStatus,
        engineer_id: form.engineer_id,
        scheduled_start: fromLocalInput(form.scheduled_start) || window.start,
        scheduled_end: fromLocalInput(form.scheduled_end) || window.end,
        site_line1: form.site_line1,
        site_line2: form.site_line2,
        site_city: form.site_city,
        site_postcode: form.site_postcode,
        site_country: form.site_country,
        site_lat: form.site_lat ?? null,
        site_lng: form.site_lng ?? null,
        description: form.description,
        notes: form.notes,
        date_completed: nextStatus === 'completed' ? (job?.date_completed || form.scheduled_start?.slice(0, 10)) : '',
      };
      if (isPendingRequest && hasSlot) {
        payload.client_requested_date = null;
        payload.client_request_note = null;
      }

      const ui = job
        ? await update('jobs', job.id, { ...job, ...payload })
        : await add('jobs', payload);

      // Photos (E2): upload after the record exists so files attach to it.
      if (photoFiles.length) {
        const fd = new FormData();
        photoFiles.forEach((file) => fd.append('photos', file));
        const record = await activePb().collection('jobs').update(ui.id, fd, { expand: 'engineer,property_id' });
        // Filenames only — URLs are minted at view time (pb.js).
        await update('jobs', ui.id, { photos: Array.isArray(record.photos) ? record.photos.filter(Boolean) : [] });
        setPhotoFiles([]);
      }

      if (variant === 'drawer') onClose?.();
      notify(job ? 'Job updated' : 'Job created');
      onSaved?.(ui);
    } catch (err) {
      notify(err.message || 'Failed to save job', 'error');
    } finally {
      setBusy(false);
    }
  };

  const clientOptions = data.clients.filter((item) => item.status !== 'archived');
  const engineerOptions = data.engineers.filter((e) => e.active !== false);

  const fields = (
    <form className="drawer-form" onSubmit={save}>
      <label>Job title<input required value={form.title} onChange={(event) => change('title', event.target.value)} placeholder="e.g. Annual boiler service" /></label>

      <label>Client
        {variant === 'drawer' && clientId ? (
          <input value={client?.name || 'Unknown client'} disabled />
        ) : (
          <select value={form.client_id} onChange={(event) => selectClient(event.target.value)}>
            <option value="">Select a client…</option>
            {clientOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        )}
      </label>

      <label>Property
        <select
          value={form.property_id}
          onChange={(event) => selectProperty(event.target.value)}
          disabled={!form.client_id || !clientProperties.length}
        >
          <option value="">{clientProperties.length ? 'No property — type the site address' : 'No properties for this client yet'}</option>
          {clientProperties.map((property) => (
            <option key={property.id} value={property.id}>{property.name || property.address_line1 || 'Unnamed property'}</option>
          ))}
        </select>
        {!clientProperties.length && form.client_id ? (
          <small className="field-hint">No properties on file — add one from the client's Properties tab, or type the site address below.</small>
        ) : null}
      </label>

      <div className="form-row">
        <label>Starts<input type="datetime-local" value={form.scheduled_start} onChange={(event) => change('scheduled_start', event.target.value)} /></label>
        <label>Ends<input type="datetime-local" value={form.scheduled_end} onChange={(event) => change('scheduled_end', event.target.value)} /></label>
      </div>

      <div className="form-row">
        <label>Status
          <select value={form.status} onChange={(event) => change('status', event.target.value)}>
            {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>Engineer
          <select value={form.engineer_id} onChange={(event) => change('engineer_id', event.target.value)}>
            <option value="">Unassigned</option>
            {engineerOptions.map((engineer) => (
              <option key={engineer.id} value={engineer.id}>{engineer.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="form-section">
        <div className="form-section-head">
          <strong>Site address</strong>
          <button type="button" className="secondary-button small-button" onClick={copyClientAddress} disabled={!client}>
            <SafeIcon icon={Icons.Clipboard} /> Same as client
          </button>
        </div>
        <AddressAutocomplete
          onSelect={applySitePlace}
          initial={siteAddressLine}
          placeholder="Search the site address…"
        />
        <label>Line 1<input value={form.site_line1} onChange={(event) => change('site_line1', event.target.value)} /></label>
        <label>Line 2<input value={form.site_line2} onChange={(event) => change('site_line2', event.target.value)} /></label>
        <div className="form-row">
          <label>Town / city<input value={form.site_city} onChange={(event) => change('site_city', event.target.value)} /></label>
          <label>Postcode<input value={form.site_postcode} onChange={(event) => change('site_postcode', event.target.value)} /></label>
        </div>
        <label>Country<input value={form.site_country} onChange={(event) => change('site_country', event.target.value)} /></label>
      </div>

      <label>Description<textarea rows="3" value={form.description} onChange={(event) => change('description', event.target.value)} /></label>
      <label>Notes<textarea rows="3" value={form.notes} onChange={(event) => change('notes', event.target.value)} /></label>

      <label>Photos
        <FileDropzone
          onFiles={setPhotoFiles}
          accept="image/jpeg,image/png,image/webp"
          hint="JPEG/PNG/WebP, up to 10 MB each — appended to existing photos"
          multiple
        />
      </label>
      {photoFiles.length ? <p className="field-hint">{photoFiles.length} photo(s) ready to attach</p> : null}

      {variant === 'drawer' ? (
        <div className="drawer-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : 'Save job'}</button>
        </div>
      ) : (
        <div className="drawer-actions">
          <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : 'Save job'}</button>
        </div>
      )}
    </form>
  );

  if (variant === 'drawer') {
    return <SlideOver open={open} title={job ? 'Edit job' : 'New job'} onClose={onClose}>{fields}</SlideOver>;
  }

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Jobs</span>
          <h1>{job ? 'Edit job' : 'New job'}</h1>
          <p className="field-hint">{job ? 'Update the job details below.' : 'Record a job for a client — link a property to prefill the site address.'}</p>
        </div>
      </header>
      <section className="panel job-form-panel">{fields}</section>
    </div>
  );
}
